// One Shadow DOM host holds all of our UI (button, panel, recording bar, annotator),
// so the site's CSS can't reach it and ours can't leak out.

import { HOST_ATTRIBUTE } from './constants.js';

export const BASE_CSS = `
:host { all: initial; }
* { box-sizing: border-box; }
.ab-root {
  --ab-accent: #1d4ed8;
  --ab-accent-text: #ffffff;
  --ab-text: #0f172a;
  --ab-muted: #475569;
  --ab-border: #cbd5e1;
  --ab-surface: #ffffff;
  --ab-subtle: #f1f5f9;
  --ab-danger: #b91c1c;
  --ab-success: #15803d;
  --ab-radius: 10px;
  --ab-shadow: 0 10px 30px rgba(15, 23, 42, 0.25), 0 2px 6px rgba(15, 23, 42, 0.12);
  font: 14px/1.45 system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--ab-text);
  -webkit-font-smoothing: antialiased;
}
button { font: inherit; cursor: pointer; }
button:disabled { cursor: not-allowed; opacity: 0.55; }
:focus-visible { outline: 3px solid color-mix(in srgb, var(--ab-accent) 55%, white); outline-offset: 2px; }
[hidden] { display: none !important; }
@media print { .ab-root { display: none !important; } }
`;

/**
 * @param {{ accent?: string }} [options]
 * @returns {{ element: HTMLElement, root: ShadowRoot, container: HTMLElement, addStyles: (css: string) => void }}
 */
export function createHost({ accent } = {}) {
  const element = document.createElement('div');
  element.setAttribute(HOST_ATTRIBUTE, '');
  element.style.cssText = 'all: initial; position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: 2147483647;';
  const root = element.attachShadow({ mode: 'open' });

  // Constructable stylesheets aren't subject to the page's Content Security Policy, so the
  // UI stays styled on sites whose style-src has no 'unsafe-inline'. <style> is the fallback.
  const addStyles = (css) => {
    if ('adoptedStyleSheets' in root && typeof CSSStyleSheet === 'function') {
      try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(css);
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
        return;
      } catch {
        // fall back to a <style> element
      }
    }
    const style = document.createElement('style');
    style.textContent = css;
    root.appendChild(style);
  };
  addStyles(BASE_CSS);

  const container = document.createElement('div');
  container.className = 'ab-root';
  if (accent) container.style.setProperty('--ab-accent', accent);
  root.appendChild(container);

  (document.body || document.documentElement).appendChild(element);
  return { element, root, container, addStyles };
}
